/**
 * Fixed user types of the agent app.
 *
 * There are no editable roles or permissions: a user's type alone decides
 * which admin-only menu items and pages they get. Everything not listed in
 * ADMIN_ONLY_MENU is open to every signed-in user.
 *
 * Keep the type values in step with App\User::TYPES on the server, which
 * enforces the same rules on the API.
 */
export type UserType = 'admin' | 'viewer'

/** Menu items and pages that only some user types get. */
export type MenuKey =
  | 'users' // User Management, Add New User
  | 'company_edit' // Edit company profile, logo, visiting card
  | 'balance_request' // Submit a balance request
  | 'notification_settings' // Notification email settings

export interface UserTypeConfig {
  label: string
  description: string
  /** Restricted menu items this type can open. */
  menu: MenuKey[]
}

export const USER_TYPES: Record<UserType, UserTypeConfig> = {
  admin: {
    label: 'Admin',
    description: 'Full access, including users, company profile, balance requests and notification settings',
    menu: ['users', 'company_edit', 'balance_request', 'notification_settings'],
  },
  viewer: {
    label: 'Viewer',
    description: 'Can browse and view everything, but cannot manage the company',
    menu: [],
  },
}

export const DEFAULT_USER_TYPE: UserType = 'viewer'

export const USER_TYPE_OPTIONS = (Object.keys(USER_TYPES) as UserType[]).map(value => ({
  value,
  label: USER_TYPES[value].label,
  description: USER_TYPES[value].description,
}))

/** Unknown or missing types are treated as the most limited one. */
export function normalizeUserType(type?: string | null): UserType {
  return type && type in USER_TYPES ? (type as UserType) : DEFAULT_USER_TYPE
}

export function userTypeLabel(type?: string | null): string {
  return USER_TYPES[normalizeUserType(type)].label
}

export function userTypeAllows(type: string | null | undefined, key: MenuKey): boolean {
  return USER_TYPES[normalizeUserType(type)].menu.includes(key)
}
